/**
 * Elevation Golf Lab — Search Overlay
 * Predictive search with localStorage recent searches, debounced fetch, body scroll lock.
 */

class SearchOverlay {
  constructor() {
    this.overlay   = document.getElementById('search-overlay');
    this.backdrop  = document.querySelector('[data-search-backdrop]');
    this.input     = document.getElementById('searchInput');
    this.body      = this.overlay?.querySelector('[data-search-body]');
    this.results   = this.overlay?.querySelector('[data-search-results]');
    this.emptyEl   = this.overlay?.querySelector('[data-search-empty]');

    if (!this.overlay) return;

    this._scrollPosition = 0;
    this._debounceTimer  = null;
    this._lastTrigger    = null;

    this._bind();
  }

  // ── Open / Close ──────────────────────────────────────────────────

  open() {
    this._scrollPosition = window.scrollY;
    document.body.style.overflow  = 'hidden';
    document.body.style.position  = 'fixed';
    document.body.style.top       = `-${this._scrollPosition}px`;
    document.body.style.width     = '100%';

    this.overlay.classList.add('is-open');
    this.overlay.setAttribute('aria-hidden', 'false');
    this.backdrop.classList.add('is-visible');

    setTimeout(() => this.input?.focus(), 100);
    this._refreshRecentSearches();
  }

  close() {
    this.overlay.classList.remove('is-open', 'is-querying');
    this.overlay.setAttribute('aria-hidden', 'true');
    this.backdrop.classList.remove('is-visible');

    document.body.style.overflow  = '';
    document.body.style.position  = '';
    document.body.style.top       = '';
    document.body.style.width     = '';
    window.scrollTo(0, this._scrollPosition);

    // Reset input without firing another fetch
    if (this.input) this.input.value = '';
    if (this.results) this.results.innerHTML = '';

    this._lastTrigger?.focus();
  }

  isOpen() {
    return this.overlay.classList.contains('is-open');
  }

  // ── Event binding ────────────────────────────────────────────────

  _bind() {
    // Open triggers
    document.querySelectorAll('[data-search-open]').forEach(btn => {
      btn.addEventListener('click', e => {
        e.preventDefault();
        this._lastTrigger = btn;
        this.open();
      });
    });
    document.addEventListener('search:open', () => this.open());

    // Close triggers
    this.backdrop?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.isOpen()) this.close();
    });
    this.overlay.addEventListener('click', e => {
      if (e.target.closest('[data-search-close]')) this.close();
    });

    // Input typing → debounced fetch
    this.input?.addEventListener('input', e => this._handleInput(e.target.value));

    // Save recent on form submit
    const form = this.overlay.querySelector('form[role="search"]');
    form?.addEventListener('submit', () => {
      const q = this.input?.value.trim();
      if (q) this._saveRecent(q);
    });

    // Remove individual recent items (delegated)
    this.overlay.addEventListener('click', e => {
      const removeBtn = e.target.closest('[data-recent-remove]');
      if (removeBtn) {
        e.preventDefault();
        e.stopPropagation();
        this._removeRecent(removeBtn.dataset.term);
        this._refreshRecentSearches();
      }
    });
  }

  // ── Input handling ───────────────────────────────────────────────

  _handleInput(value) {
    clearTimeout(this._debounceTimer);
    this._debounceTimer = setTimeout(() => {
      const q = value.trim();
      if (q.length === 0) {
        this.overlay.classList.remove('is-querying');
        this._refreshRecentSearches();
      } else {
        this.overlay.classList.add('is-querying');
        this._fetchResults(q);
      }
    }, 150);
  }

  async _fetchResults(query) {
    try {
      const url = `/search/suggest?q=${encodeURIComponent(query)}&section_id=predictive-search&resources[type]=product,article,collection&resources[limit]=4`;
      const res  = await fetch(url);
      const html = await res.text();

      const tmp     = document.createElement('div');
      tmp.innerHTML = html;

      // Shopify wraps section output in <div class="shopify-section">
      const inner = tmp.querySelector('.shopify-section')?.innerHTML ?? tmp.innerHTML;

      if (this.results) {
        this.results.innerHTML = inner;
        this._highlightMatches(this.results, query);
      }
    } catch (err) {
      console.warn('[SearchOverlay] fetch failed:', err);
    }
  }

  // Client-side case-insensitive match highlighting (improves on Liquid's case-sensitive replace)
  _highlightMatches(container, query) {
    if (!query) return;
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedQuery})`, 'gi');

    container.querySelectorAll('.search-result-product__name, .search-result-article__title').forEach(el => {
      // Only process text nodes to avoid re-processing mark tags
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
      const nodes  = [];
      let node;
      while ((node = walker.nextNode())) nodes.push(node);

      nodes.forEach(textNode => {
        if (!regex.test(textNode.textContent)) return;
        regex.lastIndex = 0; // reset after test
        const span = document.createElement('span');
        span.innerHTML = textNode.textContent.replace(regex, '<mark>$1</mark>');
        textNode.parentNode.replaceChild(span, textNode);
      });
    });
  }

  // ── Recent searches (localStorage) ───────────────────────────────

  _getRecent() {
    try {
      return JSON.parse(localStorage.getItem('eg_recent_searches') || '[]');
    } catch {
      return [];
    }
  }

  _saveRecent(term) {
    let list = this._getRecent().filter(t => t.toLowerCase() !== term.toLowerCase());
    list.unshift(term);
    localStorage.setItem('eg_recent_searches', JSON.stringify(list.slice(0, 5)));
  }

  _removeRecent(term) {
    const list = this._getRecent().filter(t => t.toLowerCase() !== term.toLowerCase());
    localStorage.setItem('eg_recent_searches', JSON.stringify(list));
  }

  _refreshRecentSearches() {
    const section    = this.overlay.querySelector('[data-recent-section]');
    const listEl     = this.overlay.querySelector('[data-recent-list]');
    if (!section || !listEl) return;

    const recent = this._getRecent();
    if (recent.length === 0) {
      section.style.display = 'none';
      return;
    }

    section.style.display = '';

    const clockIcon = `<svg class="search-suggestion__icon" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <circle cx="7" cy="7" r="5.5" stroke="currentColor" stroke-width="1.2"/>
      <path d="M7 4v3l2 1" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/>
    </svg>`;

    listEl.innerHTML = recent.map(term => `
      <li>
        <a href="/search?q=${encodeURIComponent(term)}" class="search-suggestion">
          <span class="search-suggestion__row">
            ${clockIcon}
            <span class="search-suggestion__text">${this._escape(term)}</span>
          </span>
          <button
            class="search-suggestion__remove"
            data-recent-remove
            data-term="${this._escape(term)}"
            type="button"
            aria-label="Remove ${this._escape(term)}">×</button>
        </a>
      </li>
    `).join('');
  }

  _escape(str) {
    return str.replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.searchOverlay = new SearchOverlay();
});
